/**
 * «Pagar aguinaldo»: the aguinaldo paid on its own (the owner, 2026-10-31: «let's separate the pay
 * aguinaldo function as a button on the payroll page and take that shit out of the normal payroll
 * form»). Everybody who builds it up, or has some set aside from before, each with what is set aside
 * for them and ticked when there is any; what is typed is handed over with no INSS nor income tax, out
 * of what was set aside, and past that it is an expense of the payment. Paid as it is written down:
 * the day it was and the way (`aguinaldo.pagado`).
 *
 * Opened from a payment in «Corridas», it sets it right as a run is set right: the same day and way,
 * each line as it was paid, to change, to take off or to add somebody; with nobody left, it is voided
 * (`aguinaldo.corregido`). Somebody on it settled since: it says only that.
 */
import { useState } from 'react';
import { formatMoney, hoy, newId, parseMoney, type Centavos, type CuentaBancoCordobas, type Settings } from '@abastece/shared';
import type { Empleado, PagoAguinaldo } from '@abastece/core';
import { cuentaEnCordobas, deDondeSalio } from '../lib/bancos.js';
import { emitirYVer } from '../lib/emitir.js';
import { quienesTienenAguinaldo } from '../lib/planilla.js';
import { useT } from '../i18n/index.js';
import { Campo, Selector } from './Campo.js';
import { ElegirCuenta } from './ElegirCuenta.js';
import { Ico } from './Iconos.js';
import { Modal } from './Modal.js';

/** What the sheet holds for somebody, as typed. */
interface Borrador {
  incluir: boolean;
  monto: string;
}

const enTexto = (centavos: number): string => (centavos > 0 ? (centavos / 100).toFixed(2) : '');

export function PagarAguinaldo({
  activos,
  empleados = activos,
  config,
  corrigiendo,
  onCerrar,
}: {
  activos: readonly Empleado[];
  /** Everybody, those settled too: somebody on a payment being corrected may have left since. */
  empleados?: readonly Empleado[];
  config: Settings;
  /** The payment being set right, read once as it was opened. */
  corrigiendo?: PagoAguinaldo;
  onCerrar: () => void;
}) {
  const t = useT();
  const hoyEs = hoy();

  // Those on the payment first, as it had them; then whoever else has it, by name.
  const [suLinea] = useState(() => new Map((corrigiendo?.lineas ?? []).map((l) => [l.empleadoId, l])));
  const enElPago = corrigiendo ? corrigiendo.lineas.flatMap((l) => empleados.filter((e) => e.id === l.empleadoId)) : [];
  const liquidados = enElPago.filter((e) => !e.activo).map((e) => e.nombre);
  // Somebody on it settled since: it is not corrected, and the sheet says only why.
  const bloqueado = liquidados.length > 0;
  const otros = quienesTienenAguinaldo(activos).filter((e) => !suLinea.has(e.id));
  const gente = [...enElPago.filter((e) => e.activo), ...otros];

  const [borradores, setBorradores] = useState<Record<string, Borrador>>(() =>
    Object.fromEntries([
      ...(corrigiendo?.lineas ?? []).map((l) => [l.empleadoId, { incluir: true, monto: enTexto(l.monto) }] as const),
      // Ticked when they have some set aside; on a correction, added only by hand.
      ...otros.map((e) => [e.id, { incluir: !corrigiendo && e.aguinaldoAcumulado > 0, monto: enTexto(e.aguinaldoAcumulado) }] as const),
    ]),
  );
  /** As it was opened: a correction that changes nothing is not saved. */
  const [alAbrir] = useState(borradores);
  const [fecha, setFecha] = useState(hoyEs);
  const [formaPago, setFormaPago] = useState<'efectivo_cordobas' | 'transferencia' | 'billetera'>('efectivo_cordobas');
  /** The córdoba account a payment by transfer goes out of, once picked. */
  const [cuentaPago, setCuentaPago] = useState<CuentaBancoCordobas | null>(null);
  const [nota, setNota] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [trabajando, setTrabajando] = useState(false);

  /**
   * Each line, and what it pays out of: what is set aside for them now; on a payment being
   * corrected, what was set aside when it was paid, as the books take it.
   */
  const lineas = gente
    .filter((e) => borradores[e.id]?.incluir)
    .map((e) => {
      const texto = borradores[e.id]!.monto.trim();
      return {
        empleado: e,
        monto: texto === '' ? null : parseMoney(texto),
        apartado: suLinea.get(e.id)?.apartado ?? (Math.max(0, e.aguinaldoAcumulado) as Centavos),
      };
    });
  const ilegibles = lineas.filter((l) => l.monto === null || l.monto <= 0).map((l) => l.empleado.nombre);
  const total = lineas.reduce((a, l) => a + Math.max(0, l.monto ?? 0), 0);
  const deMas = lineas.reduce((a, l) => a + Math.max(0, (l.monto ?? 0) - l.apartado), 0);
  const fechaBien = /^\d{4}-\d{2}-\d{2}$/.test(fecha) && fecha <= hoyEs;
  const cambio = JSON.stringify(borradores) !== JSON.stringify(alAbrir);
  const anulando = corrigiendo !== undefined && lineas.length === 0;
  const listo = corrigiendo
    ? cambio && !bloqueado && ilegibles.length === 0
    : lineas.length > 0 && ilegibles.length === 0 && fechaBien;
  // Corrected, what was handed over changes with it, out of or back into what paid it, on its day.
  const delPago = corrigiendo ? total - corrigiendo.total : 0;
  const pagadoCon = corrigiendo ? deDondeSalio(config, corrigiendo.formaPago, corrigiendo.cuenta, t) : '';

  const set = (id: string, cambios: Partial<Borrador>) => setBorradores((xs) => ({ ...xs, [id]: { ...xs[id]!, ...cambios } }));

  async function guardar() {
    if (!listo) return;
    setError(null);
    setTrabajando(true);
    const enviadas = lineas.map((l) => ({ empleadoId: l.empleado.id, monto: l.monto! }));
    try {
      const { rechazo } = corrigiendo
        ? await emitirYVer('aguinaldo.corregido', {
            pagoId: corrigiendo.id,
            lineas: enviadas,
            ...(nota.trim() !== '' ? { nota: nota.trim() } : {}),
          })
        : await emitirYVer('aguinaldo.pagado', {
            pagoId: newId(),
            fecha,
            lineas: enviadas,
            formaPago,
            ...(formaPago === 'transferencia' ? { cuenta: cuentaEnCordobas(config, cuentaPago) } : {}),
          });
      if (rechazo) setError(rechazo);
      else onCerrar();
    } catch (e) {
      setError(e instanceof Error ? e.message : t('planilla.noSeGuardo'));
    } finally {
      setTrabajando(false);
    }
  }

  return (
    <Modal
      titulo={t(corrigiendo ? 'planilla.aguinaldo.corregirTitulo' : 'planilla.aguinaldo.pagar')}
      onCerrar={onCerrar}
      pie={
        <div className="flex flex-col gap-2">
          {error && <p className="text-sm text-rose-400">{error}</p>}
          {bloqueado ? (
            <p className="text-sm text-amber-300" data-aguinaldo-liquidados>
              {t('planilla.aguinaldo.liquidados', { nombres: liquidados.join(', ') })}
            </p>
          ) : (
            <>
              {ilegibles.length > 0 && (
                <p className="text-xs text-amber-300" data-aguinaldo-ilegibles>
                  {t('planilla.aguinaldo.noSeEntiende', { nombres: ilegibles.join(', ') })}
                </p>
              )}
              {corrigiendo && (
                <p className="tabular text-right text-xs text-slate-500" data-aguinaldo-antes>
                  {t('planilla.aguinaldo.antes', { monto: formatMoney(corrigiendo.total) })}
                </p>
              )}
              <div className="flex items-baseline justify-between gap-3 text-sm">
                <span className="text-slate-400">{t('planilla.aguinaldo.total')}</span>
                <span className="tabular shrink-0 text-lg font-semibold whitespace-nowrap" data-aguinaldo-total>
                  {formatMoney(total as Centavos)}
                </span>
              </div>
              {deMas > 0 && (
                <p className="text-right text-xs text-amber-300" data-aguinaldo-de-mas>
                  {t('planilla.aguinaldo.totalDeMas', { monto: formatMoney(deMas as Centavos) })}
                </p>
              )}
              {anulando && cambio && (
                <p className="text-xs text-rose-300" data-aguinaldo-anulando>
                  {t('planilla.aguinaldo.anulando', { cuenta: pagadoCon })}
                </p>
              )}
              {corrigiendo && delPago !== 0 && (
                <p className="text-right text-xs text-amber-300" data-aguinaldo-pago>
                  {t(delPago > 0 ? 'planilla.aguinaldo.sale' : 'planilla.aguinaldo.vuelve', {
                    monto: formatMoney(Math.abs(delPago) as Centavos),
                    cuenta: pagadoCon,
                    fecha: corrigiendo.fecha,
                  })}
                </p>
              )}
              {corrigiendo ? (
                <button
                  type="button"
                  className={anulando ? 'btn-peligro' : 'btn-primario'}
                  disabled={trabajando || !listo}
                  data-guardar-aguinaldo
                  onClick={() => void guardar()}
                >
                  <Ico icono={anulando ? 'anular' : 'confirmar'} />
                  {t(
                    trabajando
                      ? 'planilla.corregir.guardando'
                      : !cambio
                        ? 'planilla.corregir.sinCambios'
                        : anulando
                          ? 'planilla.aguinaldo.anular'
                          : 'planilla.corregir.guardar',
                  )}
                </button>
              ) : (
                <button type="button" className="btn-primario" disabled={trabajando || !listo} data-guardar-aguinaldo onClick={() => void guardar()}>
                  <Ico icono="aguinaldo" />
                  {t(trabajando ? 'planilla.aguinaldo.pagando' : 'planilla.aguinaldo.pagar')}
                </button>
              )}
            </>
          )}
        </div>
      }
    >
      <div className="flex flex-col gap-4" data-hoja-aguinaldo>
        <p className="text-sm text-slate-400">
          {corrigiendo ? t('planilla.aguinaldo.pagado', { fecha: corrigiendo.fecha, cuenta: pagadoCon }) : t('planilla.aguinaldo.explica')}
        </p>
        {!bloqueado && corrigiendo && (
          <Campo
            etiqueta={t('planilla.corregir.porQue')}
            value={nota}
            maxLength={300}
            onChange={(ev) => setNota(ev.target.value)}
            placeholder={t('planilla.corregir.porQueEjemplo')}
          />
        )}
        {!bloqueado && gente.length === 0 && <p className="text-sm text-slate-500">{t('planilla.aguinaldo.nadie')}</p>}

        {!bloqueado &&
          gente.map((e) => {
            const b = borradores[e.id];
            if (!b) return null;
            const tenia = suLinea.get(e.id);
            const apartado = tenia?.apartado ?? (Math.max(0, e.aguinaldoAcumulado) as Centavos);
            const monto = b.monto.trim() === '' ? null : parseMoney(b.monto);
            const deMasSuyo = monto !== null && monto > apartado ? monto - apartado : 0;
            return (
              <div key={e.id} className="rounded-xl bg-slate-950/40 p-3 ring-1 ring-white/5 ring-inset" data-aguinaldo-de={e.nombre}>
                <label className="flex cursor-pointer items-center gap-3">
                  <input
                    type="checkbox"
                    checked={b.incluir}
                    onChange={(ev) => set(e.id, { incluir: ev.target.checked })}
                    className="size-5 accent-teal-500"
                  />
                  <span className="min-w-0 flex-1 truncate font-medium">{e.nombre}</span>
                  <span className="tabular shrink-0 text-sm text-slate-400">{formatMoney(apartado)}</span>
                </label>
                {corrigiendo && !tenia && b.incluir && (
                  <p className="mt-1 text-xs text-teal-300" data-aguinaldo-nuevo>
                    {t('planilla.aguinaldo.noEstaba')}
                  </p>
                )}
                {b.incluir && (
                  <div className="mt-3">
                    <Campo
                      etiqueta={t('planilla.aguinaldo')}
                      inputMode="decimal"
                      className="tabular"
                      value={b.monto}
                      onChange={(ev) => set(e.id, { monto: ev.target.value })}
                      placeholder="0.00"
                      autoComplete="off"
                      error={monto === null && b.monto.trim() !== '' ? t('caja.montoInvalido') : null}
                      ayuda={
                        tenia
                          ? t('planilla.aguinaldo.enElPago', { monto: formatMoney(tenia.monto), apartado: formatMoney(tenia.apartado) })
                          : t('planilla.aguinaldo.apartado', { monto: formatMoney(apartado) })
                      }
                    />
                    {deMasSuyo > 0 && (
                      <p className="mt-1 text-xs text-amber-300" data-aguinaldo-de-mas-de={e.nombre}>
                        {t('planilla.aguinaldo.deMas', { monto: formatMoney(deMasSuyo as Centavos) })}
                      </p>
                    )}
                  </div>
                )}
              </div>
            );
          })}

        {/* Paid as it is written down: the day it was, and how. A correction keeps both. */}
        {!corrigiendo && (
          <>
            <Campo
              etiqueta={t('planilla.sePagoEl')}
              type="date"
              value={fecha}
              max={hoyEs}
              onChange={(ev) => setFecha(ev.target.value)}
              error={fechaBien ? null : t('gastoPaso.fechaFutura')}
            />
            <Selector etiqueta={t('compra.formaPago')} value={formaPago} onChange={(ev) => setFormaPago(ev.target.value as typeof formaPago)}>
              <option value="efectivo_cordobas">{t('pago.efectivo_cordobas')}</option>
              <option value="transferencia">{t('pago.transferencia')}</option>
              <option value="billetera">{t('gastos.billeteraMovil')}</option>
            </Selector>
            {formaPago === 'transferencia' && (
              <ElegirCuenta valor={cuentaEnCordobas(config, cuentaPago)} onCambiar={(c) => setCuentaPago(c as CuentaBancoCordobas)} etiqueta={t('bancos.pagaDe')} />
            )}
            {formaPago === 'efectivo_cordobas' && <p className="text-xs text-slate-500">{t('planilla.pagoDeEfectivoGeneral')}</p>}
          </>
        )}
      </div>
    </Modal>
  );
}
